import {
  getObligationPortfolio,
  ObligationPortfolioApiError,
  type ObligationPortfolioItem,
  type ObligationPortfolioResponse,
} from '@urbanik/api-client';
import {
  Avatar,
  Button,
  CanonicalFilterBar,
  CanonicalMetricCard,
  CanonicalMetricGrid,
  DataTable,
  EmptyState,
  FilterActions,
  MoneyValue,
  PageSizeSelector,
  Pagination,
  PersonCell,
  ResultsCounter,
  SearchField,
  SelectField,
  StatePanel,
  StatusBadge,
  canonicalStates,
  type DataColumn,
} from '@urbanik/ui';
import { CircleDollarSign, FunnelX, ListChecks, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiBase } from './api-base';
type ViewState = 'loading' | 'empty' | 'error' | 'forbidden' | 'success';
const stateDef = (kind: Exclude<ViewState, 'success'>) =>
  canonicalStates.find((s) => s.kind === kind)!;
const initials = (name: string) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();
export function ObligationPortfolioView({ memberId }: { memberId?: string } = {}) {
  const [query, setQuery] = useState(''),
    [debounced, setDebounced] = useState(''),
    [conceptId, setConceptId] = useState(''),
    [periodFrom, setPeriodFrom] = useState(''),
    [periodTo, setPeriodTo] = useState(''),
    [stateFilter, setStateFilter] = useState(''),
    [page, setPage] = useState(1),
    [pageSize, setPageSize] = useState(15),
    [data, setData] = useState<ObligationPortfolioResponse | null>(null),
    [state, setState] = useState<ViewState>('loading'),
    [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(query);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);
  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    getObligationPortfolio(
      apiBase,
      {
        query: debounced,
        conceptId,
        periodFrom,
        periodTo,
        ...(stateFilter === 'confirmed' ? { state: 'confirmed' as const } : {}),
        ...(memberId ? { memberId } : {}),
        page,
        pageSize,
      },
      controller.signal,
    )
      .then((result) => {
        setData(result);
        setState(result.items.length ? 'success' : 'empty');
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        if (error instanceof ObligationPortfolioApiError && error.kind === 'unauthorized') {
          location.hash = '#login';
          return;
        }
        setState(
          error instanceof ObligationPortfolioApiError && error.kind === 'forbidden'
            ? 'forbidden'
            : 'error',
        );
      });
    return () => controller.abort();
  }, [attempt, conceptId, debounced, memberId, page, pageSize, periodFrom, periodTo, stateFilter]);
  const concepts = Array.from(
    new Map(
      (data?.items ?? []).map((item) => [
        item.conceptId,
        { value: item.conceptId, label: `${item.conceptCode} · ${item.conceptName}` },
      ]),
    ).values(),
  );
  const columns: readonly DataColumn<ObligationPortfolioItem>[] = [
    {
      id: 'member',
      header: 'Socio',
      accessor: (r) => r.memberName,
      cell: (r) => (
        <PersonCell
          avatar={<Avatar initials={initials(r.memberName)} size="small" />}
          name={r.memberName}
          secondary={r.slotNumber ? `Cupo ${r.slotNumber}` : 'Sin cupo asignado'}
        />
      ),
    },
    { id: 'nationalId', header: 'Cédula', accessor: (r) => r.nationalId || '—' },
    { id: 'concept', header: 'Concepto', accessor: (r) => `${r.conceptCode} ${r.conceptName}` },
    { id: 'period', header: 'Período', accessor: (r) => r.period },
    {
      id: 'state',
      header: 'Estado',
      accessor: () => 'Confirmada',
      cell: () => <StatusBadge tone="success">Confirmada</StatusBadge>,
    },
    {
      id: 'origin',
      header: 'Origen',
      accessor: (r) => (r.originType === 'rule' ? 'Regla' : 'Acta'),
    },
    {
      id: 'amount',
      header: 'Importe',
      accessor: (r) => r.amount,
      cell: (r) => <MoneyValue value={r.amount} tone="positive" />,
    },
  ];
  const clear = () => {
    setQuery('');
    setConceptId('');
    setPeriodFrom('');
    setPeriodTo('');
    setStateFilter('');
    setPage(1);
  };
  return (
    <section className="obligation-portfolio-view" aria-label="Cartera de obligaciones">
      <CanonicalFilterBar>
        <SearchField value={query} onChange={setQuery} />
        <SelectField
          label="Concepto"
          value={conceptId}
          onValueChange={(value) => {
            setConceptId(value);
            setPage(1);
          }}
          options={[{ value: '', label: 'Todos los conceptos' }, ...concepts]}
        />
        <label className="ui-dashboard-form-field">
          <span>Desde</span>
          <input
            type="month"
            value={periodFrom}
            onChange={(e) => {
              setPeriodFrom(e.target.value);
              setPage(1);
            }}
          />
        </label>
        <label className="ui-dashboard-form-field">
          <span>Hasta</span>
          <input
            type="month"
            value={periodTo}
            onChange={(e) => {
              setPeriodTo(e.target.value);
              setPage(1);
            }}
          />
        </label>
        <SelectField
          label="Estado"
          value={stateFilter}
          onValueChange={(value) => {
            setStateFilter(value);
            setPage(1);
          }}
          options={[
            { value: '', label: 'Todos los estados' },
            { value: 'confirmed', label: 'Confirmada' },
          ]}
        />
        <FilterActions>
          <Button icon={<FunnelX aria-hidden="true" />} onClick={clear}>
            Limpiar filtros
          </Button>
        </FilterActions>
      </CanonicalFilterBar>
      {data && !memberId ? (
        <CanonicalMetricGrid>
          <CanonicalMetricCard
            label="Obligaciones"
            value={data.totalItems}
            icon={<ListChecks aria-hidden="true" />}
          />
          <CanonicalMetricCard
            label="Total obligaciones"
            value={`$${data.totalAmount}`}
            icon={<CircleDollarSign aria-hidden="true" />}
            tone="success"
            detail="USD · cargos confirmados"
          />
          <CanonicalMetricCard
            label="En esta página"
            value={data.items.length}
            icon={<Users aria-hidden="true" />}
            tone="info"
          />
        </CanonicalMetricGrid>
      ) : null}
      {state !== 'success' ? (
        <StatePanel state={stateDef(state)} onAction={() => setAttempt((v) => v + 1)} />
      ) : data ? (
        <section className="obligation-table-card" aria-label="Listado de cartera">
          <DataTable
            paginate={false}
            rows={data.items}
            columns={columns}
            visibleColumnIds={
              new Set(
                columns
                  .filter((column) => !memberId || !['member', 'nationalId'].includes(column.id))
                  .map((column) => column.id),
              )
            }
            sort={{ columnId: 'period', direction: 'descending' }}
            onSort={() => {}}
            empty={
              <EmptyState
                title="Sin obligaciones"
                description="Ajusta los filtros para consultar otra cartera."
              />
            }
          />
          <footer>
            <ResultsCounter
              from={data.items.length ? (data.page - 1) * data.pageSize + 1 : 0}
              to={(data.page - 1) * data.pageSize + data.items.length}
              total={data.totalItems}
              noun="obligaciones"
            />
            <Pagination page={data.page} pages={Math.max(1, data.totalPages)} onChange={setPage} />
            <PageSizeSelector
              value={pageSize}
              options={[10, 15, 25, 50]}
              onChange={(value) => {
                setPageSize(value);
                setPage(1);
              }}
            />
          </footer>
        </section>
      ) : null}
    </section>
  );
}
